import type { ComponentPropsWithoutRef, FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";

export interface CardProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  title?: ReactNode;
  /** Content aligned to the right of the title (antd `extra`). */
  extra?: ReactNode;
  size?: "default" | "small";
  /** Lift on hover (shadow in light, stronger border in dark) — for clickable cards. */
  hoverable?: boolean;
  /** Replace the body with a pulsing placeholder. */
  loading?: boolean;
  headerClassName?: string;
  bodyClassName?: string;
}

/** Bordered surface: base in light, elevated in dark (as the antd Card was themed). */
export const Card: FunctionComponent<CardProps> = ({
  title,
  extra,
  size = "default",
  hoverable,
  loading,
  headerClassName,
  bodyClassName,
  className,
  children,
  ...props
}) => {
  const small = size === "small";

  return (
    <div
      className={cn(
        "rounded-lg border border-noa bg-noa-elevated text-sm text-noa",
        // A shadow is invisible on the dark background, so dark mode lifts the border instead.
        hoverable &&
          "cursor-pointer transition-[box-shadow,border-color] hover:shadow-lg hover:shadow-noa-shadow dark:hover:border-noa-strong",
        className
      )}
      {...props}
    >
      {(title || extra) && (
        <div
          className={cn(
            "flex items-center justify-between gap-2 border-b border-noa font-semibold",
            small ? "min-h-[38px] px-3" : "min-h-14 px-6 text-base",
            headerClassName
          )}
        >
          <div className="min-w-0 flex-1 truncate">{title}</div>
          {extra && <div className="shrink-0 font-normal">{extra}</div>}
        </div>
      )}
      <div className={cn(small ? "p-3" : "p-6", bodyClassName)}>
        {loading ? (
          <div className="flex animate-pulse flex-col gap-3" aria-busy="true">
            <div className="h-4 w-1/3 rounded bg-noa-hover" />
            <div className="h-4 w-full rounded bg-noa-hover" />
            <div className="h-4 w-2/3 rounded bg-noa-hover" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};
